import { useContext } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { getProjects } from '../../services/api'
import PageHeader from '../../components/ui/PageHeader'
import EmptyState from '../../components/ui/EmptyState'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import { useAggregator } from '../../contexts/AggregatorContext'
import { AuthContext } from '../../contexts/AuthContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import MyInventionsScreen from '../../components/ownscope/MyInventionsScreen'
import { resolveOwnScopeView } from '../../components/ownscope/ownScopeViews'
import AggregatorDashboard from '../../components/aggregator/AggregatorDashboard'
import HomeDashboard from '../../components/home/HomeDashboard'
// C5's invitation overlay, from the CTRL lane. ProjectsTable is deliberately
// NOT imported with it: main still routed /projects to that table behind an
// own-scope branch, and in build v2 /projects returns MyInventionsScreen above,
// so the table is unreachable from this page and the import would be dead.
import PortfolioInvitationOverlay from '../../components/sharing/PortfolioInvitationOverlay'

// Home ("/") and "My inventions" ("/projects") share one query and nothing
// else. Home is HomeDashboard, unchanged. /projects is the graduated screen of
// Story #5648 (figures S4 / S4b): one page header, one next-step banner, the
// six view tabs, and whichever view the reader asked for — MyInventionsScreen
// owns all of that, including its own loading and error states, so the chrome
// never disappears underneath a reader who asked for a view.
export default function Dashboard() {
  const location = useLocation()
  const { hasAggregatorAccess } = useAggregator()
  const { active } = useWorkspace()
  const [searchParams] = useSearchParams()
  // Bug #6683 follow-up: signed out, this asked for projects with no sign-in (a 401). Outside an AuthProvider
  // (isolated renders) the old behaviour stays.
  const auth = useContext(AuthContext)
  const signedIn = auth ? !!auth.user : true
  const { data: projects, isLoading, isError, error, refetch } = useQuery({ queryKey: ['projects'], queryFn: getProjects, enabled: signedIn })

  // Signed out, this shell route has nothing to show and Home would fire six authenticated calls with no
  // sign-in (six 401s): send the visitor to sign-in, as /search and /support do.
  // While the session is still being checked, draw nothing that asks the server for data (Home fires six
  // queries the moment it mounts, before the check finishes).
  if (auth?.loading) return <LoadingSkeleton rows={6} type="list" />
  if (auth && !auth.user) return <Navigate to="/login" replace />

  // The fork honours the ACTIVE WORKSPACE, not the raw membership count. The
  // workspace kind is the intent half and hasAggregatorAccess is the
  // authorisation half — removing either is wrong. An inventor sitting in the
  // personal workspace always gets the personal screen, whatever they belong
  // to, which is what lets an owner-keyed own scope exist at all (Story #5648).
  if (active.kind === 'aggregator' && hasAggregatorAccess) return <AggregatorDashboard />

  if (location.pathname.startsWith('/projects')) {
    return (
      <MyInventionsScreen
        view={resolveOwnScopeView(searchParams.get('view'))}
        projects={projects}
        isLoading={isLoading}
        isError={isError}
        error={error}
        refetch={() => void refetch()}
      />
    )
  }

  if (isLoading) return <LoadingSkeleton rows={6} type="list" />

  // Bug 6778: an expired or missing session answers 401, the API client signs the user out and the
  // app redirects to /login a moment later. Showing "Failed to load projects - 401" in that gap is
  // noise: keep the skeleton up until the redirect lands.
  if (isError && (error as { response?: { status?: number } } | null)?.response?.status === 401) {
    return <LoadingSkeleton rows={6} type="list" />
  }

  if (isError) {
    return (
      <div>
        <PageHeader
          title="Home"
          description="See what needs your attention and start your next action."
          helpKey="home"
        />
        <EmptyState
          icon={AlertCircle}
          title="Failed to load projects"
          description={(error as Error)?.message || 'An unexpected error occurred'}
          action={
            <button
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 rounded-lg bg-iflow-700 px-4 py-2 text-sm font-medium text-white hover:bg-iflow-800"
            >
              <RefreshCw size={16} /> Retry
            </button>
          }
        />
      </div>
    )
  }

  // Home. The v1 own-scope branch main still carries here is gone: /projects
  // returned MyInventionsScreen at the top of this function, which owns the
  // rail, the tabs and both S4/S4b views, so mounting them again here would
  // double-render them.
  return (
    <>
      <HomeDashboard projects={projects ?? []} />
      {/* Figure C5 draws the invitation over HOME. It arrives unbidden the
          moment an organisation invites the account you are signed in as, so it
          has to be able to interrupt the page you are already on rather than be
          somewhere you navigate to. Renders nothing without ?accept. */}
      <PortfolioInvitationOverlay />
    </>
  )
}
